import React, { useState, memo } from 'react';
import { Grid, _ } from 'gridjs-react';
import { KeyValue } from '../types/KeyValue';
import { Props as ProjectListStaffProps } from '../types/ProjectListStaff';
import { project_list_staff_read_index as read_index } from '../Const';
import { TCell } from 'gridjs/dist/src/types';

type Props = {
  projects: Array<KeyValue[]>;
  config: ProjectListStaffProps['config'];
  handleClick: (
    event: React.MouseEvent<HTMLAnchorElement>,
    id: string,
    r_cd: string,
    status: number
  ) => void;
  handleRowChange: (index: string) => void;
};

const ResultList: React.FC<Props> = ({ projects, config, handleClick, handleRowChange }) => {
  const [rows, setRows] = useState(10);

  const onChangeLimit = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setRows(Number(event.target.value));
  };

  /**
   * 日付ソート用の定義
   * Note：Grid.js と併用する前提で作成しています。
   *
   * @param {TCell} target 対象要素
   * @param {TCell} comparison 比較要素
   * @return {*}  {number}
   */
  const sortForDate = (target: TCell, comparison: TCell): number => {
    const code = (value: string | null | undefined): Date | null => {
      return typeof value !== 'undefined' && value !== null ? new Date(value) : null;
    };
    const date_target = code(target?.toString());
    const date_comparison = code(comparison?.toString());

    // 値がNULLの場合の判定
    if (date_target === null) {
      return -1;
    } else if (date_comparison === null) {
      return 1;
    }

    // 日付の判定
    if (date_target > date_comparison) {
      return 1;
    } else if (date_target < date_comparison) {
      return -1;
    } else {
      return 0;
    }
  };

  return (
    <>
      <div className="row my-2">
        <div className="col-md-12">
          <span className="search-criteria-head">表示件数</span>
          <select
            name="表示件数"
            className="search-form-input"
            onChange={onChangeLimit}
            value={rows}
          >
            {config.display_list_number.map((num: number, index: number) => {
              return (
                <option key={index} value={num}>
                  {num}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <Grid
        data={projects}
        columns={[
          {
            id: 'myCheckbox',
            name: '',
            formatter: (cell, row) =>
              _(
                <input
                  className="row-selection"
                  type="checkbox"
                  value={`${row.cells[read_index.reception_cd].data}`}
                  key={`${row.cells[read_index.reception_cd].data}`}
                  onChange={() => handleRowChange(String(row.cells[read_index.reception_cd].data))}
                />
              ),
            width: '10px',
          },
          {
            id: 'subscription_id',
            name: '案件番号',
            formatter: (cell, row) => {
              if (
                !config.disabled_project_detail_status.includes(
                  Number(row.cells[read_index.status].data)
                )
              ) {
                return _(
                  <a
                    className="link-text"
                    href={`${location.origin}${process.env.MIX_MOCK_URL}/reception/${
                      row.cells[read_index.reception_cd].data
                    }/${row.cells[read_index.reception_id].data}`}
                    onClick={(event: React.MouseEvent<HTMLAnchorElement>) =>
                      handleClick(
                        event,
                        String(row.cells[read_index.reception_id].data),
                        String(row.cells[read_index.reception_cd].data),
                        Number(row.cells[read_index.status].data)
                      )
                    }
                  >
                    {row.cells[read_index.reception_cd].data as React.ReactNode}
                  </a>
                );
              } else {
                return _(<>{row.cells[read_index.reception_cd].data}</>);
              }
            },
            width: '100px',
          },
          {
            id: 'reception_store',
            name: '受付店',
            width: '100px',
          },
          {
            id: 'retailer',
            name: '取扱店',
            width: '100px',
          },
          {
            id: 'execution_store',
            name: '実行店',
            width: '100px',
          },
          {
            id: 'kana_name',
            name: 'カナ氏名',
            width: '150px',
          },
          {
            id: 'kanji_name',
            name: '漢字氏名',
            width: '150px',
          },
          {
            id: 'birthday',
            name: '生年月日',
            sort: {
              compare: (target, comparison) => sortForDate(target, comparison),
            },
            width: '150px',
          },
          {
            id: 'address_1',
            name: 'ご住所（県市）',
            width: '180px',
          },
          {
            id: 'address_2',
            name: 'ご住所（続き）（全角）',
            width: '250px',
          },
          {
            id: 'residence_1',
            name: '住居表示　住所（県市）',
            width: '220px',
          },
          {
            id: 'residence_2',
            name: '住居表示　住所（続き）（全角）',
            width: '280px',
          },
          {
            id: 'pre_examination_date',
            name: '事前審査申込入力日時',
            sort: {
              compare: (target, comparison) => sortForDate(target, comparison),
            },
            width: '250px',
          },
          {
            id: 'item_name',
            name: '商品名',
            width: '150px',
          },
          {
            id: 'status',
            name: 'ステータス',
            width: '180px',
            sort: {
              compare: (target, comparison) => {
                // 案件ステータス抜出し
                const code = (str?: string) => {
                  const matched = str?.match(/^[0-9]+/);
                  return matched ? Number(matched[0]) : 0;
                };

                const numTarget = code(target?.toString());
                const numComparison = code(comparison?.toString());

                if (numTarget > numComparison) {
                  return 1;
                } else if (numComparison > numTarget) {
                  return -1;
                } else {
                  return 0;
                }
              },
            },
          },
          {
            id: 'status_updated_at',
            name: 'ステータス更新日',
            width: '180px',
            sort: {
              compare: (target, comparison) => sortForDate(target, comparison),
            },
          },
          // 以下は画面に表示しないデータ（カラム）
          {
            id: 'id',
            hidden: true,
          },
          {
            id: 'status',
            hidden: true,
          },
        ]}
        search={false}
        sort={true}
        pagination={{
          enabled: true,
          limit: rows,
        }}
        fixedHeader={true}
        height="600px"
      />
    </>
  );
};

export default memo(ResultList);
